import {
  AiAuditReport,
  parseAuditReport
} from "@domain/ai_answers/audit_report"
import { CustomerIdData } from "@domain/customer/infos"
import { ApiFormattedResponse } from "@models/api_formatted_response"
import { mutate, query } from "@utils/graphql"
import {
  AiAnswerExplanation,
  AiAnswerExplanationResponse,
  AiMessagesPaginate,
  AiMessagesResponse,
  AiMessageSteps,
  AiMessageStepsResponse,
  AllChannelTypeResponse,
  AnalyzeAiMessageRunInput,
  AnalyzeAiMessageRunResponse,
  ChannelCommonTypes,
  CreateRandomMessageInput,
  DeleteByDateRangeParams,
  DeleteResult,
  DeleteResultResponse,
  ExplainAiMessageStepInput,
  ExplainAiMessageStepResponse,
  ListInfiniteParams,
  OCustomerPersonStringResponse
} from "__generated__/graphql"
import createRandomMessage from "__generated__/queries/mutations/createRandomMessage"
import deleteAiMessagesByDateRange from "__generated__/queries/mutations/deleteAiMessagesByDateRange"
import analyzeAiMessageRun from "__generated__/queries/queries/analyzeAiMessageRun"
import explainAiMessageStep from "__generated__/queries/queries/explainAiMessageStep"
import getAiAnswerExplanation from "__generated__/queries/queries/getAiAnswerExplanation"
import getAllConnectedPlatform from "__generated__/queries/queries/getAllConnectedPlatform"
import listAiConversations from "__generated__/queries/queries/listAiConversations"
import listAiMessages from "__generated__/queries/queries/listAiMessages"
import listAiMessageSteps from "__generated__/queries/queries/listAiMessageSteps"

export async function listAiMessagesQuery(
  params: ListInfiniteParams
): Promise<ApiFormattedResponse<AiMessagesPaginate>> {
  const response = await query<{
    listAiMessages?: AiMessagesResponse
  }>({
    query: listAiMessages,
    variables: {
      data: {
        conversationId: params.conversationId,
        customerId: params.customerId,
        limit: params.limit,
        offset: params.offset,
        ...(params.search ? { search: params.search } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.channelType ? { channelType: params.channelType } : {}),
        ...(params.kind ? { kind: params.kind } : {}),
        ...(params.startDate && params.endDate
          ? { startDate: params.startDate }
          : {}),
        ...(params.endDate && params.startDate
          ? { endDate: params.endDate }
          : {})
      }
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.listAiMessages?.data
  }
}

export async function listAiConversationsQuery(
  params: ListInfiniteParams
): Promise<ApiFormattedResponse<AiMessagesPaginate>> {
  const response = await query<{
    listAiConversations?: AiMessagesResponse
  }>({
    query: listAiConversations,
    variables: {
      data: {
        conversationId: params.conversationId,
        customerId: params.customerId,
        limit: params.limit,
        offset: params.offset,
        ...(params.search ? { search: params.search } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.channelType ? { channelType: params.channelType } : {}),
        ...(params.kind ? { kind: params.kind } : {}),
        ...(params.startDate && params.endDate
          ? { startDate: params.startDate }
          : {}),
        ...(params.endDate && params.startDate
          ? { endDate: params.endDate }
          : {})
      }
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.listAiConversations?.data
  }
}

export async function deleteAiMessages(
  data: DeleteByDateRangeParams
): Promise<ApiFormattedResponse<DeleteResult>> {
  const response = await mutate<{
    deleteAiMessagesByDateRange?: DeleteResultResponse
  }>({
    mutation: deleteAiMessagesByDateRange,
    variables: { data: data }
  })
  if (!response.isSuccess) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response.data?.deleteAiMessagesByDateRange?.data
  }
}

export async function listAiMessageStepsQuery(
  aiMessageId: string,
  answerId?: string
): Promise<ApiFormattedResponse<AiMessageSteps[]>> {
  const response = await query<{
    listAiMessageSteps?: AiMessageStepsResponse
  }>({
    query: listAiMessageSteps,
    variables: { aiMessageId, answerId }
  })
  if (!response.isSuccess || !response.data?.listAiMessageSteps?.data) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.listAiMessageSteps?.data as AiMessageSteps[]
  }
}

/** Why a run answered the way it did — the same explanation the user panel shows its tenant. */
export async function getAiAnswerExplanationQuery(
  messageId: string
): Promise<ApiFormattedResponse<AiAnswerExplanation>> {
  const response = await query<{
    getAiAnswerExplanation?: AiAnswerExplanationResponse
  }>({
    query: getAiAnswerExplanation,
    variables: { messageId },
    fetchPolicy: "network-only"
  })
  const explanation = response.data?.getAiAnswerExplanation
  if (!response.isSuccess || !explanation?.success || !explanation.data) {
    return {
      isSuccess: false,
      errorMessage: explanation?.message ?? response.errorMessage
    }
  }
  const data: AiAnswerExplanation = explanation.data
  return { isSuccess: true, data }
}

export async function explainAiMessageStepQuery(
  data: ExplainAiMessageStepInput
): Promise<ApiFormattedResponse<AiAuditReport>> {
  const response = await query<{
    explainAiMessageStep?: ExplainAiMessageStepResponse
  }>({
    query: explainAiMessageStep,
    variables: { data }
  })
  const report = parseAuditReport(response.data?.explainAiMessageStep?.data)
  if (!response.isSuccess || !report) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: report }
}

export async function analyzeAiMessageRunQuery(
  data: AnalyzeAiMessageRunInput
): Promise<ApiFormattedResponse<AiAuditReport>> {
  const response = await query<{
    analyzeAiMessageRun?: AnalyzeAiMessageRunResponse
  }>({
    query: analyzeAiMessageRun,
    variables: { data }
  })
  const report = parseAuditReport(response.data?.analyzeAiMessageRun?.data)
  if (!response.isSuccess || !report) {
    return { ...response, data: undefined }
  }
  return { isSuccess: true, data: report }
}

export async function createRandomMessageMutation(
  data: CreateRandomMessageInput,
  apiUrl?: string
): Promise<ApiFormattedResponse<string>> {
  const response = await mutate<{
    createRandomMessage?: OCustomerPersonStringResponse
  }>({
    mutation: createRandomMessage,
    variables: { data },
    context: apiUrl ? { uri: `${apiUrl}/graphql` } : undefined
  })
  if (!response.isSuccess || !response.data?.createRandomMessage?.data) {
    return { ...response, data: undefined }
  }
  return {
    isSuccess: true,
    data: response?.data?.createRandomMessage?.data
  }
}

export async function channelsGetAllPlatform(
  data: CustomerIdData
): Promise<ApiFormattedResponse<ChannelCommonTypes[]>> {
  const response = await query<
    {
      getAllConnectedPlatform?: AllChannelTypeResponse
    },
    { customerId: string }
  >({
    query: getAllConnectedPlatform,
    variables: {
      customerId: data.customerId
    }
  })
  if (!response.isSuccess) {
    return { ...response, data: [] }
  }

  return {
    isSuccess: true,
    data: response.data?.getAllConnectedPlatform?.data as ChannelCommonTypes[]
  }
}
